import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, BookOpen } from 'lucide-react';
import { Topic } from '../../types/topic';
import CardMetadata from './CardMetadata';

interface SectionCardProps {
  topic: Topic;
  variant?: 'default' | 'compact' | 'detailed';
  showProgress?: boolean;
  showStats?: boolean;
  progressPercent?: number;
  className?: string;
}

/**
 * Generic reusable card component for displaying topics in sections
 * Supports multiple variants for different use cases
 */
const SectionCard: React.FC<SectionCardProps> = ({
  topic,
  variant = 'default',
  showProgress = false,
  showStats = false,
  progressPercent = 0,
  className = '',
}) => {
  const hasModules = Number.isFinite(topic.subtopicsCount) && topic.subtopicsCount > 0;
  const hasEstimatedTime = Boolean(topic.estimatedTime?.trim());

  const getDifficultyBadgeClass = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'badge-green';
      case 'intermediate':
        return 'badge-yellow';
      case 'advanced':
        return 'badge-red';
      default:
        return 'badge-gray';
    }
  };

  // Default variant - Standard card with icon, title, description, and difficulty
  if (variant === 'default') {
    return (
      <Link
        to={topic.link}
        className={`group card p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 ${className}`}
      >
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-primary-100 to-sky-100 dark:from-primary-900/30 dark:to-sky-900/30 rounded-lg flex items-center justify-center text-primary-600 dark:text-primary-400">
            <BookOpen className="section-card-topic-icon w-5 h-5" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
              {topic.title}
            </h3>
          </div>
        </div>

        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {topic.description}
        </p>

        <div className="flex items-center justify-between">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyBadgeClass(topic.difficulty)}`}>
            {topic.difficulty.charAt(0).toUpperCase() + topic.difficulty.slice(1)}
          </span>
          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
        </div>
      </Link>
    );
  }

  // Compact variant - Smaller card for dense layouts
  if (variant === 'compact') {
    return (
      <Link
        to={topic.link}
        className={`group card p-4 hover:shadow-md transition-all duration-300 hover:-translate-y-1 ${className}`}
      >
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-primary-100 to-sky-100 dark:from-primary-900/30 dark:to-sky-900/30 rounded-lg flex items-center justify-center text-lg">
            <BookOpen className="w-5 h-5" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-1 truncate">
              {topic.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-xs truncate">
              {topic.description}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600 transition-all" />
        </div>
      </Link>
    );
  }

  // Detailed variant - Full card with stats, prerequisites
  return (
    <Link
      to={topic.link}
      className={`catalog-card group relative rounded-xl bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col overflow-hidden ${className}`}
    >
      {/* Top accent strip */}
      <div className="card-accent-strip h-[3px] w-full bg-gray-200 dark:bg-gray-700 group-hover:bg-primary-400 dark:group-hover:bg-primary-500 transition-colors" />

      <div className="p-5 flex flex-col flex-1">
        {/* Icon + Title */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-2xl flex-shrink-0 group-hover:border-primary-200 dark:group-hover:border-primary-800 transition-colors">
            <BookOpen className="w-4 h-4" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white leading-snug group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
              {topic.title}
            </h3>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2 mb-4">
          {topic.description}
        </p>

        {/* Show only metadata that has a meaningful value. */}
        <CardMetadata
          items={[
            { label: 'Modules', value: hasModules ? topic.subtopicsCount : null },
            { label: 'Time', value: hasEstimatedTime ? topic.estimatedTime : null },
          ]}
        />

        {/* Prerequisites */}
        {topic.prerequisites && topic.prerequisites.length > 0 && (
          <div className="catalog-card__prerequisites section-card-prerequisites mb-4">
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-500">Prerequisites</span>
            <div className="catalog-card__prerequisite-items section-card-prerequisites__items flex flex-wrap gap-1.5 mt-1.5">
              {topic.prerequisites.slice(0, 2).map((prereq: string, idx: number) => (
                <span key={idx} className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                  {prereq}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Spacer to push footer down */}
        <div className="flex-1" />

        {/* Footer — difficulty + CTA */}
        <div className="catalog-card__footer section-card-footer flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700 mt-auto">
          <span className={`inline-block text-[11px] font-semibold px-2.5 py-1 rounded-md ${getDifficultyBadgeClass(topic.difficulty)}`}>
            {topic.difficulty.charAt(0).toUpperCase() + topic.difficulty.slice(1)}
          </span>
          <div className="flex items-center text-primary-600 dark:text-primary-400 text-sm font-medium group-hover:translate-x-0.5 transition-transform">
            Learn Now <ChevronRight className="w-4 h-4 ml-0.5" />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default SectionCard;
